import { dateToIso, isoToDate } from "@/modules/recursos/equipe-obra.rules";
import { createElement, useMemo, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { useToast } from "@/ds";
import type { SearchMenuHandle } from "@/ds";
import { useObraEmbarcacaoInformacoes, useObraPeriodos } from "@/modules/obras";
import {
  calcularCronograma,
  chaveErroPeriodo,
  DURACAO_DIAS_MINIMO,
  isDuracaoValida,
  readDuracao,
  temPeriodoObra,
} from "../hooks/nova-obra.duracao";
import { periodoDaObraNoWizard } from "../hooks/nova-obra.equipe";
import { useNovaObra } from "../hooks/nova-obra.context";
import type { DadosDuracao, PeriodoLinha } from "../hooks/nova-obra.duracao";
import { CARREGANDO, DURACAO_EMPTY, DURACAO_LABELS, DURACAO_TOAST } from "../nova-obra.constants";
import { alinharPainelAoBotao } from "@/shared/components/alinhar-painel/alinhar-painel";
import { DesfazerToastDetalhe } from "../components/desfazer-toast-detalhe";
import { formatDataBr } from "../components/format-data";
import { MENU_PERIODOS_VAZIO } from "./etapa-duracao.constants";
import { formatDataBrOuVazio, paraNumero } from "./etapa-duracao.utils";
import type { LinhaView } from "./types-etapa-duracao";

/** Id da linha: o estado mora no wizard e sobrevive à tela, então um contador do módulo repetiria ids (HMR, remontagem). */
const novoUid = () =>
  `periodo-${globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`}`;

export function useEtapaDuracao() {
  const { state, errosVisiveis, salvarEtapa } = useNovaObra();
  const toast = useToast();
  const [verLimites, setVerLimites] = useState(false);
  const dados = useMemo(() => readDuracao(state.etapas.duracao), [state.etapas.duracao]);
  const dadosRef = useRef(dados);
  dadosRef.current = dados;
  const menu = useRef<SearchMenuHandle>(null);
  const botaoMenu = useRef<HTMLElement | null>(null);
  const [menuAberto, setMenuAberto] = useState(false);
  const periodos = useObraPeriodos();
  const informacoes = useObraEmbarcacaoInformacoes(state.obra.embarcacaoId);
  const cronograma = useMemo(() => calcularCronograma(dados), [dados]);
  const periodoWizard = useMemo(
    () => periodoDaObraNoWizard(state.etapas.duracao),
    [state.etapas.duracao],
  );
  const opcoes = useMemo(
    () =>
      periodos.data.map((periodo) => ({
        value: periodo.id,
        label: periodo.descricao,
        obra: periodo.etapa === "OBRA", // RN04: a View só desenha a badge; quem classifica é o VM.
      })),
    [periodos.data],
  );
  const opcoesMenu = useMemo(
    () =>
      opcoes.map(({ value, label, obra }) => ({
        id: value,
        label,
        obra,
        testId: `periodo-opcao-${value}`,
      })),
    [opcoes],
  );
  const linhas = useMemo<LinhaView[]>(
    () =>
      dados.periodos.map((linha, indice) => {
        const calculado = cronograma.linhas[indice];
        const numero = indice + 1;
        const carregandoTipo = periodos.status === "loading" && Boolean(linha.periodoObraId);
        const opcaoDoTipo = opcoes.find((opcao) => opcao.value === linha.periodoObraId);

        return {
          ...linha,
          indice,
          inicio: calculado?.inicio ?? null,
          fim: calculado?.fim ?? null,
          erroDuracao:
            errosVisiveis[chaveErroPeriodo(linha.uid, "duracao")] ??
            // Duração 0 é inválida já em tela; vazia só é cobrada ao tentar avançar.
            (linha.duracao !== null && !isDuracaoValida(linha.duracao)
              ? DURACAO_DIAS_MINIMO
              : undefined),
          tipoRotulo: opcaoDoTipo?.label ?? (carregandoTipo ? CARREGANDO : ""),
          tipoObra: opcaoDoTipo?.obra ?? false,
          tipoTestId: `periodo-tipo-${indice}`,
          inicioTexto: formatDataBrOuVazio(calculado?.inicio),
          fimTexto: formatDataBrOuVazio(calculado?.fim),
          duracaoTexto: linha.duracao === null ? "" : String(linha.duracao),
          metaTexto: linha.meta === null ? "" : String(linha.meta),
          inicioAria: `Início do período ${numero}`,
          fimAria: `Fim do período ${numero}`,
          duracaoAria: `Duração (d) do período ${numero}`,
          metaAria: `Meta (dias) do período ${numero}`,
          removerAria: `${DURACAO_LABELS.removerPeriodo} ${numero}`,
        };
      }),
    [dados, cronograma, periodos.status, errosVisiveis, opcoes],
  ); // O DataTable memoriza as linhas: as ações leem o estado mais recente pela ref, não pelo render.
  const salvar = (proximo: DadosDuracao) => salvarEtapa("duracao", proximo);
  const atualizarLinha = (uid: string, campos: Partial<PeriodoLinha>) => {
    const atual = dadosRef.current;
    salvar({
      ...atual,
      periodos: atual.periodos.map((linha) =>
        linha.uid === uid ? { ...linha, ...campos } : linha,
      ),
    });
  }; // Figma `01c`: "Adicionar Período" abre o menu com busca; a linha só entra com o período escolhido.
  const alterarDuracao = (uid: string, texto: string) =>
    atualizarLinha(uid, { duracao: paraNumero(texto) });
  const alterarMeta = (uid: string, texto: string) =>
    atualizarLinha(uid, { meta: paraNumero(texto) });
  const adicionarPeriodo = (periodoObraId: string) => {
    const atual = dadosRef.current;
    salvar({
      ...atual,
      // RN02: o novo período vai ao final; o mesmo tipo pode repetir (RN15).
      periodos: [...atual.periodos, { uid: novoUid(), periodoObraId, duracao: null, meta: null }],
    });
  };
  const alternarMenu = (event: MouseEvent<HTMLElement>) => {
    botaoMenu.current = event.currentTarget;
    menu.current?.toggle(event);
  };
  const abrirMenu = () => {
    setMenuAberto(true);
    alinharPainelAoBotao(menu.current?.getElement(), botaoMenu.current);
  };
  const fecharMenu = () => {
    setMenuAberto(false);
    // Item escolhido ou Esc: o foco volta ao botão. Num clique fora o foco já está onde a pessoa clicou.
    const ativo = document.activeElement;
    const painel = menu.current?.getElement();
    if (!ativo || ativo === document.body || painel?.contains(ativo)) botaoMenu.current?.focus();
  };
  const desfazer = (linha: PeriodoLinha, indice: number) => {
    const atual = dadosRef.current;
    if (atual.periodos.some((item) => item.uid === linha.uid)) return;

    const restaurados = [...atual.periodos];
    restaurados.splice(Math.min(indice, restaurados.length), 0, linha);
    salvar({ ...atual, periodos: restaurados });
    toast.clear();
  }; // RN09: remoção imediata, sem confirmação; o toast oferece Desfazer com os dados da linha.
  const removerPeriodo = (linha: PeriodoLinha, indice: number) => {
    const atual = dadosRef.current;
    const tipo = periodos.data.find((periodo) => periodo.id === linha.periodoObraId);
    const nome = tipo?.descricao ?? DURACAO_TOAST.semNome;
    const { uid, periodoObraId, duracao, meta } = linha;

    salvar({ ...atual, periodos: atual.periodos.filter((item) => item.uid !== uid) });
    toast.show({
      severity: "secondary",
      summary: DURACAO_TOAST.summary,
      life: 8000,
      detail: createElement(DesfazerToastDetalhe, {
        detalhe: DURACAO_TOAST.detail(nome),
        rotulo: DURACAO_TOAST.desfazer,
        onDesfazer: () => desfazer({ uid, periodoObraId, duracao, meta }, indice),
      }),
    });
  };
  const inicioHabilitado = dados.inicioObra !== ""; // Figma `10415:40506`: o aviso só aparece com período(s) escolhido(s) e nenhum deles é Obra; uma
  // linha recém-adicionada, ainda sem tipo, não dispara o aviso.
  const semPeriodoObra =
    dados.periodos.some((linha) => linha.periodoObraId !== null && linha.periodoObraId !== "") &&
    periodos.status === "success" &&
    !temPeriodoObra(dados, periodos.data);
  const consultaPeriodosFalhou = periodos.status === "forbidden" || periodos.status === "error";
  const embarcacaoId = state.obra.embarcacaoId;

  return {
    errosVisiveis,
    verLimites: verLimites && Boolean(embarcacaoId),
    abrirLimites: () => setVerLimites(true),
    fecharLimites: () => setVerLimites(false),
    embarcacaoId,
    dados,
    menu,
    menuAberto,
    alertaPeriodos: consultaPeriodosFalhou ? periodos.errorMessage : null,
    catalogo: periodos.data,
    informacoesLimites: informacoes.status === "success" ? informacoes.data : null,
    cronograma,
    periodoWizard,
    opcoesMenu,
    mensagemMenuVazio: periodos.status === "loading" ? CARREGANDO : MENU_PERIODOS_VAZIO,
    linhas,
    estatisticas: [
      { label: DURACAO_LABELS.inicioResumo, valor: formatDataBr(cronograma.inicio ?? "") },
      {
        label: DURACAO_LABELS.terminoPrevisto,
        valor: formatDataBr(cronograma.terminoPrevisto ?? ""),
      },
      {
        label: DURACAO_LABELS.duracaoTotal,
        valor:
          cronograma.duracaoTotal === null
            ? "—"
            : `${cronograma.duracaoTotal} ${cronograma.duracaoTotal === 1 ? "dia" : "dias"}`,
      },
    ],
    alterarDuracao,
    alterarMeta,
    adicionarPeriodo,
    alternarMenu,
    abrirMenu,
    fecharMenu,
    removerPeriodo,
    inicioHabilitado,
    inicioVazio: !inicioHabilitado,
    semPeriodoObra,
    semPeriodos: dados.periodos.length === 0,
    mensagemSemPeriodos: inicioHabilitado ? DURACAO_EMPTY.comInicio : DURACAO_EMPTY.semInicio,
    inicioObraData: isoToDate(dados.inicioObra),
    alterarInicioObra: (data: Date | null | undefined | (Date | null)[]) =>
      salvar({ ...dados, inicioObra: data instanceof Date ? dateToIso(data) : "" }),
  };
}
